// ── Workflow Setup ────────────────────────────────────────────
// Multi-step flow: Context → Goal → Workflow Canvas → Run
// This is the new session creation experience.

import React, { useState, useCallback, useRef, useMemo, memo } from 'react';
import { ReactFlow, Background, Controls, MiniMap, BackgroundVariant, Handle, Position, MarkerType, ReactFlowProvider, getBezierPath, BaseEdge } from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import { authFetch } from './api';
import { MODES } from './modeConfig';
import PatternPalette from './PatternPalette';
import IntakeInterview from './IntakeInterview';
import BriefReview from './BriefReview';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Step indicators ──────────────────────────────────────────
// Interview + Brief are the guided-intake steps (SPEC-GUIDED-INTAKE.md).
// Both are conditionally skipped (readiness ≥ 8, user skip, autopilot,
// or any interview-endpoint failure) — the step indicator still shows
// them so the flow reads as one journey.

const STEPS = [
  { id: 'goal',      label: 'Goal',      icon: '🎯' },
  { id: 'interview', label: 'Clarify',   icon: '💬' },
  { id: 'brief',     label: 'Brief',     icon: '📋' },
  { id: 'plan',      label: 'Plan',      icon: '🔀' },
];

function StepIndicator({ currentStep, steps }) {
  const currentIdx = steps.findIndex(s => s.id === currentStep);
  return (
    <div className="wfs-steps">
      {steps.map((step, i) => (
        <div key={step.id} className={`wfs-step ${i <= currentIdx ? 'wfs-step--active' : ''} ${i === currentIdx ? 'wfs-step--current' : ''}`}>
          <span className="wfs-step-icon">{step.icon}</span>
          <span className="wfs-step-label">{step.label}</span>
          {i < steps.length - 1 && <span className="wfs-step-arrow">→</span>}
        </div>
      ))}
    </div>
  );
}

// ── Context source cards ─────────────────────────────────────

function ContextCard({ source, onRemove }) {
  const icons = { file: '📄', gdoc: '📝', github: '⟨/⟩', text: '📋' };
  return (
    <div className="wfs-context-card">
      <span className="wfs-context-card-icon">{icons[source.type] || '📎'}</span>
      <div className="wfs-context-card-info">
        <span className="wfs-context-card-name">{source.name}</span>
        <span className="wfs-context-card-detail">{source.detail}</span>
      </div>
      <button className="wfs-context-card-remove" onClick={onRemove}>✕</button>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────

export default function WorkflowSetup({ sessionId, initialMode, initialGoal = '', onWorkflowReady, onSkip }) {
  const initialModeObj = initialMode ? MODES.find(m => m.id === initialMode) : null;
  const [step, setStep] = useState('goal');
  const [contextSources, setContextSources] = useState([]);
  const [contextText, setContextText] = useState('');
  const [goal, setGoal] = useState(initialGoal || initialModeObj?.goalTemplate || '');
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('');
  const [workflow, setWorkflow] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [githubUrl, setGithubUrl] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [autopilot, setAutopilot] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  // ── Guided intake state (SPEC-GUIDED-INTAKE.md, single-page rev) ──
  const [interviewRounds, setInterviewRounds] = useState([]); // transcript: [{round, questions, answers}] — round 1 = the form, round 2 = goldfish finals (max)
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [interviewTitle, setInterviewTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [goldfish, setGoldfish] = useState(null);
  const [recommendedPatterns, setRecommendedPatterns] = useState([]); // answer-driven, from /api/interview/brief
  const [intakeMeta, setIntakeMeta] = useState(null); // persisted as session meta.intake
  const finalRoundUsedRef = useRef(false); // goldfish may trigger AT MOST one extra round

  // ── File upload handler ──────────────────────────────────

  const handleFileUpload = useCallback(async (files) => {
    if (!files?.length || !sessionId) return;

    const formData = new FormData();
    for (const file of files) formData.append('files', file);

    try {
      const res = await authFetch(`${API_URL}/api/sessions/${sessionId}/files`, {
        method: 'POST',
        body: formData,
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch {
        throw new Error(res.status === 503 ? 'Server is temporarily unavailable — the file may be too large. Try a smaller file.' : `Upload failed (${res.status})`);
      }
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      if (data.files) {
        const newSources = data.files.map(f => ({
          type: 'file',
          name: f.name,
          detail: `${(f.size / 1024).toFixed(1)} KB — ${f.textLength || 0} chars extracted`,
          extractedText: f.extractedText || '',
        }));
        setContextSources(prev => [...prev, ...newSources]);

        // Fetch combined context
        const ctxRes = await authFetch(`${API_URL}/api/sessions/${sessionId}/files/context`);
        const ctxData = await ctxRes.json();
        if (ctxData.context) setContextText(ctxData.context);
      }
      // Surface per-file errors and rejected files
      const warnings = [];
      if (data.errors?.length) {
        warnings.push(...data.errors.map(e => `${e.name}: ${e.error}`));
      }
      if (data.rejected?.length) {
        warnings.push(...data.rejected);
      }
      if (warnings.length) setError(warnings.join('; '));
    } catch (err) {
      setError('Failed to upload files: ' + err.message);
    }
  }, [sessionId]);

  // ── GitHub repo handler ──────────────────────────────────

  const handleGithubAttach = useCallback(async () => {
    if (!githubUrl.trim()) return;
    setLoading(true);
    setLoadingLabel('Fetching repository...');

    try {
      const res = await authFetch(`${API_URL}/api/analyze-github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: githubUrl.trim(), analysisGoals: ['features'] }),
      });

      // Read SSE response to get analysis text
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let repoText = '';
      let repoName = githubUrl.trim().split('/').slice(-2).join('/');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && line !== 'data: [DONE]') {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.label) repoText += `[${parsed.type}] ${parsed.label}: ${parsed.reasoning || ''}\n`;
              if (parsed._progress) setLoadingLabel(parsed._progress);
            } catch { /* skip */ }
          }
        }
      }

      setContextSources(prev => [...prev, {
        type: 'github',
        name: repoName,
        detail: `${repoText.split('\n').filter(Boolean).length} elements analyzed`,
        extractedText: repoText,
      }]);
      setContextText(prev => prev + '\n\n[GitHub: ' + repoName + ']\n' + repoText);
      setGithubUrl('');
    } catch (err) {
      setError('Failed to fetch repo: ' + err.message);
    } finally {
      setLoading(false);
      setLoadingLabel('');
    }
  }, [githubUrl]);

  // ── Paste text handler ───────────────────────────────────

  const handlePasteAdd = useCallback(() => {
    if (!pasteText.trim()) return;
    const preview = pasteText.trim().slice(0, 60) + (pasteText.length > 60 ? '...' : '');
    setContextSources(prev => [...prev, {
      type: 'text',
      name: preview,
      detail: `${pasteText.length} chars`,
      extractedText: pasteText,
    }]);
    setContextText(prev => prev + '\n\n[Pasted text]\n' + pasteText);
    setPasteText('');
    setShowPaste(false);
  }, [pasteText]);

  // ── Remove context source ────────────────────────────────

  const handleRemoveSource = useCallback((idx) => {
    setContextSources(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      // Rebuild context text from remaining sources
      const newText = next.map(s => s.extractedText || '').join('\n\n');
      setContextText(newText);
      return next;
    });
  }, []);

  // ── Orchestrate: goal → workflow ─────────────────────────
  // briefText, when present, prefixes the context so the orchestrator
  // receives the approved Design Brief as its primary specification.

  // opts.autoRun: brief-approved path skips the Plan gate — the approved
  // contract IS the approval, so the workflow starts the moment the DAG
  // returns (contract-loop.feature: "Brief approval auto-runs the workflow").
  // opts.intakeMeta passed explicitly because setIntakeMeta hasn't flushed
  // by the time orchestrate resolves.
  const runOrchestrate = useCallback(async (briefText = null, opts = {}) => {
    if (!goal.trim()) return;
    setLoading(true);
    setError(null);

    // Two-pass orchestrator (draft + critique) takes 60-180s on complex goals.
    // Surface progress through staged labels + live elapsed counter so the
    // user knows it's working. Server.requestTimeout is 15 min so plenty of
    // headroom — but we still show a hint at 90s that it's still going.
    const t0 = Date.now();
    let labelStage = 0;
    const stages = [
      'Reading your goal + context...',
      'Drafting the workflow (Opus pass 1, extended thinking)...',
      'Critiquing + refining (Opus pass 2)...',
      'Still going — complex goals take longer...',
    ];
    setLoadingLabel(stages[0]);
    const labelTimer = setInterval(() => {
      const elapsed = Math.round((Date.now() - t0) / 1000);
      const idx = elapsed < 8 ? 0 : elapsed < 60 ? 1 : elapsed < 120 ? 2 : 3;
      if (idx !== labelStage) labelStage = idx;
      setLoadingLabel(`${stages[idx]} (${elapsed}s)`);
    }, 1000);

    try {
      const effectiveContext = briefText
        ? `${briefText}\n\n---\n\n${contextText || ''}`.trim()
        : (contextText || null);
      const res = await authFetch(`${API_URL}/api/orchestrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal: goal.trim(),
          context: effectiveContext,
          contextSources: contextSources.map(s => ({ type: s.type, name: s.name, detail: s.detail })),
          // Answer-driven pattern picks from the intake interview — the
          // orchestrator weaves their stages into the DAG. Only sent on
          // the brief-approved path (empty when interview skipped).
          recommendedPatterns: briefText ? recommendedPatterns : [],
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setWorkflow(data.workflow);
      if (opts.autoRun) {
        // Contract signed → run now. Autopilot config not applied here:
        // the brief path is interactive by definition (autopilot skips it).
        onWorkflowReady?.({
          workflow: data.workflow,
          goal,
          contextText,
          contextSources,
          intakeMeta: opts.intakeMeta || null,
        });
      } else {
        setStep('plan');
      }
    } catch (err) {
      setError('Failed to generate workflow: ' + err.message);
    } finally {
      clearInterval(labelTimer);
      setLoading(false);
      setLoadingLabel('');
    }
  }, [goal, contextText, contextSources, recommendedPatterns, onWorkflowReady]);

  // ── Guided intake handlers (SPEC-GUIDED-INTAKE.md §4.5) ───

  // Skip helper: record why, run today's direct-orchestrate path.
  const skipToOrchestrate = useCallback((skipReason, readiness = null) => {
    setIntakeMeta({ skipped: true, skipReason, readiness, rounds: 0, transcript: [], brief: null, goldfish: null });
    runOrchestrate(null);
  }, [runOrchestrate]);

  // Goal submit — interview gate. Autopilot always skips. Any endpoint
  // failure fails open to direct orchestrate (never block creation).
  const handleGoalSubmit = useCallback(async () => {
    if (!goal.trim()) return;
    if (autopilot) return skipToOrchestrate('autopilot');

    setLoading(true);
    setLoadingLabel('Thinking about what to ask…');
    setError(null);
    try {
      const res = await authFetch(`${API_URL}/api/interview/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          goal: goal.trim(),
          context: contextText || null,
          contextSources: contextSources.map(s => ({ type: s.type, name: s.name, detail: s.detail })),
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      if (data.decision === 'ready') {
        setLoading(false);
        setLoadingLabel('');
        return skipToOrchestrate(data.failedOpen ? 'error' : 'readiness', data.readiness);
      }

      setCurrentQuestions(data.questions || []);
      setInterviewTitle(data.title || '');
      setInterviewRounds([]);
      finalRoundUsedRef.current = false;
      setLoading(false);
      setLoadingLabel('');
      setStep('interview');
    } catch (err) {
      console.warn('[intake] start failed — falling back to direct orchestrate:', err.message);
      setLoading(false);
      setLoadingLabel('');
      skipToOrchestrate('error');
    }
  }, [goal, autopilot, sessionId, contextText, contextSources, skipToOrchestrate, runOrchestrate]);

  // Compile brief + run goldfish. finalQuestions → one extra round max.
  const requestBrief = useCallback(async (transcript) => {
    setLoading(true);
    setLoadingLabel('Compiling your brief…');
    try {
      const labelTimer = setTimeout(() => setLoadingLabel('Goldfish is reading it cold…'), 6000);
      const res = await authFetch(`${API_URL}/api/interview/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, goal: goal.trim(), context: contextText || null, transcript }),
      });
      clearTimeout(labelTimer);
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      if (data.goldfish?.finalQuestions?.length && !finalRoundUsedRef.current) {
        // Major gaps → exactly one extra (short) form, then re-brief.
        finalRoundUsedRef.current = true;
        setCurrentQuestions(data.goldfish.finalQuestions);
        setInterviewTitle('A couple of follow-ups');
        setLoading(false);
        setLoadingLabel('');
        setStep('interview');
        return;
      }

      setBrief(data.brief);
      setGoldfish(data.goldfish);
      setRecommendedPatterns(data.recommendedPatterns || []);
      setLoading(false);
      setLoadingLabel('');
      setStep('brief');
    } catch (err) {
      console.warn('[intake] brief failed — falling back to direct orchestrate:', err.message);
      setLoading(false);
      setLoadingLabel('');
      setIntakeMeta({ skipped: true, skipReason: 'error', readiness: null, rounds: transcript.length, transcript, brief: null, goldfish: null });
      runOrchestrate(null);
    }
  }, [sessionId, goal, contextText, runOrchestrate]);

  // Form submitted → straight to brief. No per-round trip: the
  // questionnaire is single-page (all questions answered at once).
  const handleInterviewSubmit = useCallback(async (answers) => {
    const newTranscript = [...interviewRounds, { round: interviewRounds.length + 1, questions: currentQuestions, answers }];
    setInterviewRounds(newTranscript);
    await requestBrief(newTranscript);
  }, [interviewRounds, currentQuestions, requestBrief]);

  // Brief approved (possibly edited) → orchestrate with brief-prefixed context.
  const handleBriefApprove = useCallback((editedBrief) => {
    const meta = {
      skipped: false,
      skipReason: null,
      readiness: null,
      rounds: interviewRounds.length,
      transcript: interviewRounds,
      brief: editedBrief,
      goldfish: goldfish ? { verdict: goldfish.verdict, gaps: goldfish.gaps } : null,
      recommendedPatterns,
    };
    setIntakeMeta(meta);
    runOrchestrate(editedBrief, { autoRun: true, intakeMeta: meta });
  }, [interviewRounds, goldfish, recommendedPatterns, runOrchestrate]);

  // ── Refine workflow via chat ──────────────────────────────

  const handleRefineWorkflow = useCallback(async (instruction) => {
    if (!workflow || !instruction.trim()) return;
    setLoading(true);
    setLoadingLabel('Updating workflow...');

    try {
      const res = await authFetch(`${API_URL}/api/orchestrate/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow, instruction: instruction.trim() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setWorkflow(data.workflow);
    } catch (err) {
      setError('Failed to refine: ' + err.message);
    } finally {
      setLoading(false);
      setLoadingLabel('');
    }
  }, [workflow]);

  // ── Run workflow ──────────────────────────────────────────

  const handleRun = useCallback(() => {
    if (!workflow) return;
    const wf = autopilot
      ? { ...workflow, config: { ...workflow.config, autopilot: true } }
      : workflow;
    onWorkflowReady?.({
      workflow: wf,
      goal,
      contextText,
      contextSources,
      intakeMeta, // guided-intake record → persisted as session meta.intake
    });
  }, [workflow, autopilot, goal, contextText, contextSources, intakeMeta, onWorkflowReady]);

  // ── Drag-and-drop ────────────────────────────────────────

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // ── Splice pattern into workflow at an edge ──────────────

  const handleSplicePattern = useCallback(async (edgeFrom, edgeTo, patternId) => {
    if (!workflow) return;
    try {
      const res = await authFetch(`${API_URL}/api/patterns/${patternId}`);
      const data = await res.json();
      const patternDef = data.versions?.[0]?.definition || data;
      if (!patternDef?.stages || !patternDef?.graph) return;

      const prefix = patternDef.id + '_';
      const newWorkflow = JSON.parse(JSON.stringify(workflow));

      // Add namespaced stages
      for (const [name, def] of Object.entries(patternDef.stages)) {
        newWorkflow.stages[prefix + name] = def;
      }

      // Remove the original edge
      newWorkflow.graph.edges = newWorkflow.graph.edges.filter(
        e => !(e.from === edgeFrom && e.to === edgeTo)
      );

      // Find entrypoint and terminal stages of the pattern
      const entrypoint = patternDef.graph.entrypoint;
      const allTargets = new Set((patternDef.graph.edges || []).map(e => e.to));
      const allSources = new Set((patternDef.graph.edges || []).map(e => e.from));
      const stageNames = Object.keys(patternDef.stages);
      // Terminal = stages that are never a source in edges, or marked terminal
      let terminals = stageNames.filter(s => !allSources.has(s));
      if (terminals.length === 0) terminals = [stageNames[stageNames.length - 1]];

      // Wire: edgeFrom -> pattern entrypoint
      newWorkflow.graph.edges.push({ from: edgeFrom, to: prefix + entrypoint });

      // Wire: pattern terminals -> edgeTo
      for (const t of terminals) {
        newWorkflow.graph.edges.push({ from: prefix + t, to: edgeTo });
      }

      // Add internal pattern edges (namespaced)
      for (const edge of (patternDef.graph.edges || [])) {
        newWorkflow.graph.edges.push({ from: prefix + edge.from, to: prefix + edge.to });
      }

      // Namespace fan_out references if present
      if (patternDef.graph.fan_out) {
        if (!newWorkflow.graph.fan_out) newWorkflow.graph.fan_out = {};
        for (const [name, def] of Object.entries(patternDef.graph.fan_out)) {
          newWorkflow.graph.fan_out[prefix + name] = {
            ...def,
            branches: (def.branches || []).map(b => prefix + b),
            mergeTo: def.mergeTo ? prefix + def.mergeTo : undefined,
          };
        }
      }

      setWorkflow(newWorkflow);
    } catch (err) {
      setError('Failed to splice pattern: ' + err.message);
    }
  }, [workflow]);

  // ── Example goals ────────────────────────────────────────

  const EXAMPLE_GOALS = contextSources.length > 0
    ? [
        'Generate 10 unique product ideas based on this material',
        'Audit this for logical gaps and propose fixes',
        'Extract key insights and organize into a strategy framework',
        'Break this into actionable tasks with priorities',
      ]
    : [
        'Brainstorm 10 creative SaaS ideas for developers',
        'Design a go-to-market strategy for an AI product',
        'Map out the competitive landscape for fintech',
        'Create a product roadmap for a mobile health app',
      ];

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="wfs">
      <StepIndicator currentStep={step} steps={STEPS} />

      {error && (
        <div className="wfs-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* ── STEP 1: Goal (with optional context expander) ──── */}
      {step === 'goal' && (
        <div className="wfs-panel wfs-goal">
          <h2 className="wfs-title">What do you want to accomplish?</h2>
          <p className="wfs-subtitle">Describe your goal and we'll design a custom thinking workflow.</p>

          <textarea
            className="wfs-goal-input"
            placeholder="Come up with 10 wildly creative startup ideas that leverage our existing codebase..."
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={4}
            autoFocus
          />

          <div className="wfs-examples">
            <span className="wfs-examples-label">Try:</span>
            {EXAMPLE_GOALS.map((eg, i) => (
              <button key={i} className="wfs-example-chip" onClick={() => setGoal(eg)}>
                {eg}
              </button>
            ))}
          </div>

          <div className="wfs-mode-pills">
            <span className="wfs-examples-label">Or start from a mode:</span>
            <div className="wfs-mode-pills-row">
              {MODES.filter(m => !m.hidden && m.goalTemplate).map(m => (
                <button
                  key={m.id}
                  className={`wfs-mode-pill${goal === m.goalTemplate ? ' wfs-mode-pill--active' : ''}`}
                  style={{ '--mode-color': m.color }}
                  onClick={() => setGoal(m.goalTemplate)}
                >
                  <span className="wfs-mode-pill-icon">{m.icon}</span>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Collapsible context expander ──────────────── */}
          <div className="wfs-context-expander">
            <button
              className={`wfs-context-toggle ${showContext ? 'wfs-context-toggle--open' : ''}`}
              onClick={() => setShowContext(c => !c)}
            >
              <span className="wfs-context-toggle-icon">{showContext ? '▾' : '▸'}</span>
              <span>📎 Add context</span>
              {contextSources.length > 0 && (
                <span className="wfs-context-badge">{contextSources.length}</span>
              )}
            </button>

            {showContext && (
              <div className="wfs-context-body">
                <div
                  className="wfs-dropzone wfs-dropzone--compact"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <span className="wfs-dropzone-icon">📁</span>
                  <span className="wfs-dropzone-text">Drop files here or click to browse</span>
                  <span className="wfs-dropzone-hint">PDF, DOCX, TXT, MD, code files, spreadsheets</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.txt,.md,.json,.yaml,.yml,.js,.ts,.jsx,.tsx,.py,.go,.rs,.java,.feature"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e.target.files)}
                  />
                </div>

                <div className="wfs-context-sources">
                  <button className="wfs-source-btn" onClick={() => setShowPaste(!showPaste)}>
                    <span>📋</span> Paste text or URL
                  </button>
                  <div className="wfs-source-btn-group">
                    <input
                      className="wfs-github-input"
                      type="text"
                      placeholder="Paste GitHub URL..."
                      value={githubUrl}
                      onChange={(e) => setGithubUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleGithubAttach()}
                    />
                    <button
                      className="wfs-source-btn wfs-source-btn--go"
                      onClick={handleGithubAttach}
                      disabled={!githubUrl.trim() || loading}
                    >
                      ⟨/⟩ Attach repo
                    </button>
                  </div>
                </div>

                {showPaste && (
                  <div className="wfs-paste-area">
                    <textarea
                      className="wfs-paste-textarea"
                      placeholder="Paste text, URLs, notes..."
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      rows={3}
                    />
                    <button className="wfs-btn wfs-btn--sm" onClick={handlePasteAdd} disabled={!pasteText.trim()}>
                      Add to context
                    </button>
                  </div>
                )}

                {contextSources.length > 0 && (
                  <div className="wfs-context-cards">
                    <div className="wfs-context-cards-header">
                      <span>{contextSources.length} source{contextSources.length !== 1 ? 's' : ''} attached</span>
                      <span className="wfs-context-cards-tokens">{contextText.length.toLocaleString()} chars</span>
                    </div>
                    {contextSources.map((s, i) => (
                      <ContextCard key={i} source={s} onRemove={() => handleRemoveSource(i)} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {loading && <div className="wfs-loading"><span className="wfs-spinner" /> {loadingLabel}</div>}

          <div className="wfs-nav">
            <button className="wfs-btn wfs-btn--ghost" onClick={onSkip}>Skip</button>
            <button
              className="wfs-btn wfs-btn--primary"
              onClick={handleGoalSubmit}
              disabled={!goal.trim() || loading}
            >
              Plan workflow →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Interview (guided intake) ───────────────── */}
      {step === 'interview' && (
        <div className="wfs-panel wfs-interview">
          {loading ? (
            <div className="wfs-loading wfs-iv-loading"><span className="wfs-spinner" /> {loadingLabel}</div>
          ) : (
            <IntakeInterview
              title={interviewTitle}
              questions={currentQuestions}
              onSubmit={handleInterviewSubmit}
              onSkip={() => skipToOrchestrate('user')}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* ── STEP 3: Brief review (goldfish gate) ────────────── */}
      {step === 'brief' && brief && (
        <div className="wfs-panel wfs-brief-panel">
          <BriefReview
            brief={brief}
            goldfish={goldfish}
            recommendedPatterns={recommendedPatterns}
            onApprove={handleBriefApprove}
            onBack={() => setStep('goal')}
            loading={loading}
          />
          {loading && <div className="wfs-loading"><span className="wfs-spinner" /> {loadingLabel}</div>}
        </div>
      )}

      {/* ── STEP 4: Plan (Workflow Canvas) ──────────────────── */}
      {step === 'plan' && workflow && (
        <div className="wfs-panel wfs-plan">
          <div className="wfs-plan-header">
            <div>
              <h2 className="wfs-title">{workflow.icon || '🔀'} {workflow.name || 'Your Workflow'}</h2>
              <p className="wfs-subtitle">{workflow.description || goal}</p>
            </div>
            <div className="wfs-plan-actions">
              <button className="wfs-btn wfs-btn--ghost" onClick={() => setStep('goal')}>← Edit goal</button>
              <button
                className="wfs-btn wfs-btn--ghost"
                onClick={() => setPaletteCollapsed(c => !c)}
                title="Toggle pattern palette"
              >
                {paletteCollapsed ? '◧ Patterns' : '◧'}
              </button>
              <label className="wfs-autopilot-toggle" title="Auto-pilot: run, evaluate, re-plan until quality threshold is met">
                <input type="checkbox" checked={autopilot} onChange={(e) => setAutopilot(e.target.checked)} />
                <span>Auto-pilot</span>
              </label>
              <button className="wfs-btn wfs-btn--ghost" onClick={() => setShowSchedule(true)} title="Schedule recurring execution">
                Schedule
              </button>
              <button className="wfs-btn wfs-btn--run" onClick={handleRun}>▶ Run</button>
            </div>
          </div>

          <div className="wfs-plan-body">
            <WorkflowBlocks
              workflow={workflow}
              loading={loading}
              loadingLabel={loadingLabel}
              onRefine={handleRefineWorkflow}
              onSplicePattern={handleSplicePattern}
            />
            <PatternPalette
              collapsed={paletteCollapsed}
              onToggle={() => setPaletteCollapsed(c => !c)}
              onAddPattern={(name) => {
                handleRefineWorkflow(`Add the "${name}" thinking pattern to this workflow`);
              }}
              loading={loading}
            />
          </div>

          {showSchedule && (
            <ScheduleModal
              workflow={workflow}
              goal={goal}
              contextText={contextText}
              onClose={() => setShowSchedule(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Schedule Modal ──────────────────────────────────────────

const SCHEDULE_PRESETS = [
  { label: 'Daily at 9am', cron: '0 9 * * *' },
  { label: 'Weekly Monday', cron: '0 9 * * 1' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Every 12 hours', cron: '0 */12 * * *' },
];

function ScheduleModal({ workflow, goal, contextText, onClose }) {
  const [selectedCron, setSelectedCron] = useState('');
  const [customCron, setCustomCron] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const activeCron = customCron || selectedCron;

  const handleSchedule = async () => {
    if (!activeCron) return;
    setScheduling(true);
    setScheduleError(null);
    try {
      const res = await authFetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: workflow.name || 'Scheduled Workflow',
          type: 'workflow',
          prompt: goal,
          schedule: { cron: activeCron },
          config: { workflow, goal, contextText },
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setScheduled(true);
    } catch (err) {
      setScheduleError(err.message);
    } finally {
      setScheduling(false);
    }
  };

  if (scheduled) {
    return (
      <div className="wfs-schedule-overlay" onClick={onClose}>
        <div className="wfs-schedule-modal" onClick={(e) => e.stopPropagation()}>
          <div className="wfs-schedule-success">
            <span className="wfs-schedule-success-icon">✓</span>
            <p>Workflow scheduled successfully!</p>
            <p className="wfs-schedule-success-cron">{activeCron}</p>
            <button className="wfs-btn wfs-btn--primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wfs-schedule-overlay" onClick={onClose}>
      <div className="wfs-schedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wfs-schedule-header">
          <h3>Schedule Workflow</h3>
          <button className="pp-close" onClick={onClose}>✕</button>
        </div>

        <p className="wfs-schedule-desc">
          Run "{workflow.name || 'this workflow'}" on a recurring schedule.
        </p>

        <div className="wfs-schedule-presets">
          {SCHEDULE_PRESETS.map(p => (
            <button
              key={p.cron}
              className={`wfs-schedule-preset ${selectedCron === p.cron && !customCron ? 'wfs-schedule-preset--active' : ''}`}
              onClick={() => { setSelectedCron(p.cron); setCustomCron(''); }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="wfs-schedule-custom">
          <label className="wfs-schedule-custom-label">Custom cron:</label>
          <input
            className="wfs-schedule-custom-input"
            placeholder="e.g., 0 */4 * * *"
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
          />
        </div>

        {scheduleError && <div className="pp-generate-error">{scheduleError}</div>}

        <div className="wfs-schedule-actions">
          <button className="wfs-btn wfs-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            className="wfs-btn wfs-btn--primary"
            onClick={handleSchedule}
            disabled={!activeCron || scheduling}
          >
            {scheduling ? 'Scheduling...' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Stage type config ────────────────────────────────────────

const STAGE_ICONS = {
  generate: '⚡', transform: '⚙', score: '📊', branch: '⑂',
  fan_out: '⤡', merge: '⊕', filter: '⧩', enrich: '🔍', loop: '↻',
  refine: '🔄', fractal: '🌀',
};

const STAGE_COLORS = {
  generate: '#6c63ff', transform: '#f59e0b', score: '#22c55e', branch: '#ff4757',
  fan_out: '#ec4899', merge: '#0ea5e9', filter: '#f97316', enrich: '#14b8a6', loop: '#cc5de8',
  refine: '#a855f7', fractal: '#06b6d4',
};

// ── Custom node for React Flow canvas ───────────────────────

const NODE_WIDTH = 240;

const WorkflowStageNode = memo(({ data }) => {
  const color = STAGE_COLORS[data.stageType] || '#666';
  const icon = STAGE_ICONS[data.stageType] || '◈';

  return (
    <div className="wfc-node" style={{ borderColor: color }}>
      <Handle type="target" position={Position.Left} className="wfc-handle" />

      <div className="wfc-node-header">
        <span className="wfc-node-icon" style={{ background: color }}>{icon}</span>
        <span className="wfc-node-name">{data.label}</span>
      </div>

      <div className="wfc-node-meta">
        <span className="wfc-node-type" style={{ color }}>{data.stageType}</span>
        {data.model && <span className="wfc-node-model">{data.model.split(':')[1] || data.model}</span>}
      </div>

      {data.prompt && (
        <p className="wfc-node-prompt">{data.prompt}</p>
      )}

      {data.branches && data.branches.length > 0 && (
        <div className="wfc-node-branches">
          {data.branches.filter(Boolean).map(b => (
            <span key={b} className="wfc-node-branch" style={{ borderColor: color }}>{String(b).replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}

      {data.dimensions && (
        <div className="wfc-node-dims">
          {data.dimensions.filter(d => d && d.name).map(d => (
            <span key={d.name} className="wfc-node-dim">{d.name.replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}

      <Handle type="source" position={Position.Right} className="wfc-handle" />
    </div>
  );
});
WorkflowStageNode.displayName = 'WorkflowStageNode';

const nodeTypes = { stage: WorkflowStageNode };

// ── Custom edge with drop zone for pattern splicing ─────────

function SpliceableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data }) {
  const [edgePath] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const [hovering, setHovering] = useState(false);

  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setHovering(false);
    try {
      const raw = e.dataTransfer.getData('application/pattern-splice');
      if (!raw) return;
      const { patternId } = JSON.parse(raw);
      if (patternId && data?.onSplice) {
        data.onSplice(data.sourceId, data.targetId, patternId);
      }
    } catch {}
  }, [data]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setHovering(true);
  }, []);

  return (
    <>
      <BaseEdge path={edgePath} style={style} markerEnd={markerEnd} />
      <foreignObject
        x={midX - 14}
        y={midY - 14}
        width={28}
        height={28}
        className="spliceable-edge-fo"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setHovering(false)}
      >
        <div className={`spliceable-edge-drop ${hovering ? 'spliceable-edge-drop--hover' : ''}`}>
          +
        </div>
      </foreignObject>
    </>
  );
}

const edgeTypes = { spliceable: SpliceableEdge };

// ── Build React Flow nodes & edges from workflow ────────────

function buildFlowGraph(workflow, onSplicePattern) {
  if (!workflow?.stages || !workflow?.graph) return { nodes: [], edges: [] };

  const rawNodes = [];
  const rawEdges = [];
  const fanOutDefs = workflow.graph.fan_out || {};

  // Collect all stage nodes
  for (const [name, def] of Object.entries(workflow.stages)) {
    rawNodes.push({
      id: name,
      type: 'stage',
      position: { x: 0, y: 0 },
      data: {
        label: name.replace(/_/g, ' '),
        stageType: def.type,
        model: def.model,
        prompt: def.promptFallback ? def.promptFallback.slice(0, 100) + (def.promptFallback.length > 100 ? '...' : '') : null,
        branches: def.type === 'fan_out' ? def.branches : null,
        dimensions: def.type === 'score' ? def.dimensions : null,
      },
    });
  }

  // Add virtual fan_out nodes
  for (const [name, def] of Object.entries(fanOutDefs)) {
    if (!workflow.stages[name]) {
      rawNodes.push({
        id: name,
        type: 'stage',
        position: { x: 0, y: 0 },
        data: {
          label: name.replace(/_/g, ' '),
          stageType: 'fan_out',
          branches: def.branches,
        },
      });
    }
  }

  // Build edges from graph.edges
  for (const edge of (workflow.graph.edges || [])) {
    rawEdges.push({
      id: `e-${edge.from}-${edge.to}`,
      source: edge.from,
      target: edge.to,
      type: onSplicePattern ? 'spliceable' : 'default',
      data: onSplicePattern ? { sourceId: edge.from, targetId: edge.to, onSplice: onSplicePattern } : undefined,
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#444' },
      style: { stroke: '#444', strokeWidth: 1.5 },
      animated: false,
    });
  }

  // Color fan_out edges
  for (const [fanName, fanDef] of Object.entries(fanOutDefs)) {
    const color = STAGE_COLORS.fan_out;
    for (const branch of (fanDef.branches || [])) {
      const edgeId = `e-${fanName}-${branch}`;
      const existing = rawEdges.find(e => e.id === edgeId);
      if (existing) {
        existing.style = { stroke: color, strokeWidth: 2 };
        existing.markerEnd = { ...existing.markerEnd, color };
        existing.animated = true;
      }
    }
    // Color merge edges
    if (fanDef.mergeTo) {
      for (const branch of (fanDef.branches || [])) {
        const edgeId = `e-${branch}-${fanDef.mergeTo}`;
        const existing = rawEdges.find(e => e.id === edgeId);
        if (existing) {
          existing.style = { stroke: STAGE_COLORS.merge, strokeWidth: 2, strokeDasharray: '6 3' };
          existing.markerEnd = { ...existing.markerEnd, color: STAGE_COLORS.merge };
        }
      }
    }
  }

  // Auto-layout with dagre
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 80, marginx: 40, marginy: 40 });

  for (const node of rawNodes) {
    const hasPrompt = node.data.prompt ? 1 : 0;
    const hasBranches = node.data.branches?.length || 0;
    const hasDims = node.data.dimensions?.length || 0;
    const height = 56 + (hasPrompt ? 32 : 0) + (hasBranches ? 28 : 0) + (hasDims ? 28 : 0);
    g.setNode(node.id, { width: NODE_WIDTH, height });
  }

  for (const edge of rawEdges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  const layoutNodes = rawNodes.map(node => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - pos.height / 2 },
    };
  });

  return { nodes: layoutNodes, edges: rawEdges };
}

// ── Workflow Canvas: React Flow DAG view ────────────────────

function WorkflowCanvasInner({ workflow, loading, loadingLabel, onRefine, onSplicePattern }) {
  const [refineInput, setRefineInput] = useState('');

  const { nodes, edges } = useMemo(() => buildFlowGraph(workflow, onSplicePattern), [workflow, onSplicePattern]);

  if (!workflow?.stages) return null;

  return (
    <div className="wfc">
      <div className="wfc-canvas">
        {loading && (
          <div className="wfc-loading-overlay">
            <span className="wfs-spinner" />
            <span>{loadingLabel || 'Updating workflow...'}</span>
          </div>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3, duration: 400 }}
          minZoom={0.3}
          maxZoom={1.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#0c0c14' }}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#1a1a2a" />
          <Controls
            showInteractive={false}
            style={{ background: '#16161f', border: '1px solid #2a2a3a', borderRadius: '8px', overflow: 'hidden' }}
          />
          <MiniMap
            nodeColor={(n) => STAGE_COLORS[n.data?.stageType] || '#444'}
            maskColor="rgba(10,10,15,0.85)"
            style={{ background: '#111118', border: '1px solid #2a2a3a', borderRadius: '8px' }}
          />
        </ReactFlow>
      </div>

      <div className="wfc-refine">
        <input
          className="wfc-refine-input"
          placeholder="Tweak the workflow... (e.g., 'Add a critique step before ranking')"
          value={refineInput}
          onChange={(e) => setRefineInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && refineInput.trim() && !loading) {
              onRefine(refineInput);
              setRefineInput('');
            }
          }}
          disabled={loading}
        />
        <button
          className="wfc-refine-btn"
          onClick={() => { onRefine(refineInput); setRefineInput(''); }}
          disabled={!refineInput.trim() || loading}
        >
          Refine
        </button>
      </div>
    </div>
  );
}

function WorkflowBlocks(props) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
